import axios from "axios";
import { SystemAnnouncement, SystemAnnouncementCreateData, SystemAnnouncementsResponse, SystemAnnouncementUpdateData } from "../../types/features/administration/SystemAnnouncement";
import { BACKEND_URL } from "../../config/api";


export const systemAnnouncementService = {
  async getAll(
    token: string,
    skip: number = 0,
    limit: number = 50,
    statusFilter?: "ACTIVE" | "DESACTIVE"
  ): Promise<SystemAnnouncementsResponse> {
    try {
      const params: any = { skip, limit };
      if (statusFilter) {
        params.status_filter = statusFilter;
      }

      const response = await axios.get(`${BACKEND_URL}/system-announcements/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async getById(token: string, id: string): Promise<SystemAnnouncement> {
    try {
      const response = await axios.get(`${BACKEND_URL}/system-announcements/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data?.data;
    } catch (error) {
      throw error;
    }
  },

  async create(token: string, data: SystemAnnouncementCreateData): Promise<SystemAnnouncement> {
    const hasDescription = !!(data.description && data.description.trim());
    const hasImage = data.image instanceof File;

    if (!hasDescription && !hasImage) {
      throw new Error("Either description or image must be provided");
    }

    // 1. On prépare l'URL avec les paramètres (ce qui faisait marcher l'image avant)
    const params = new URLSearchParams({
      title: data.title,
      status: data.status,
      is_block: String(data.is_block),
    });
    if (hasDescription) params.append("description", data.description!);

    const url = `${BACKEND_URL}/system-announcements/?${params.toString()}`;

    // 2. On prépare le corps de la requête proprement
    const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
    let body: FormData | object = {};

    if (hasImage) {
      const formData = new FormData();
      formData.append("image", data.image!);
      body = formData; // Axios gère le Content-Type tout seul ici
    } else {
      body = {}; // Un objet vide évite l'erreur 400 sur la description seule
      headers["Content-Type"] = "application/json";
    }

    // Pas de try/catch ici (le linter sera content), l'erreur remontera toute seule
    const response = await axios.post(url, body, { headers });
    return response.data?.data;
  },

  async update(token: string, id: string, data: SystemAnnouncementUpdateData): Promise<SystemAnnouncement> {
    // 1. Préparer les paramètres d'URL (Query Params)
    const params = new URLSearchParams();
    if (data.title) params.append("title", data.title);
    if (data.status) params.append("status", data.status);
    if (data.is_block !== undefined) params.append("is_block", String(data.is_block));
    if (data.description) params.append("description", data.description);

    const queryString = params.toString();
    const url = queryString
      ? `${BACKEND_URL}/system-announcements/${id}?${queryString}`
      : `${BACKEND_URL}/system-announcements/${id}`;

    // 2. Préparer les headers et le corps (Body)
    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    let body: FormData | object = {};

    if (data.image instanceof File) {
      const formData = new FormData();
      formData.append("image", data.image);
      body = formData;
      // Note: On ne force pas le Content-Type pour le FormData, Axios le fait très bien
    } else {
      // Si pas d'image, on envoie un objet vide et on précise JSON
      body = {};
      headers["Content-Type"] = "application/json";
    }

    // 3. Appel API (L'erreur remontera d'elle-même vers le Hook)
    const response = await axios.put(url, body, { headers });
    return response.data?.data;
  },

  async delete(token: string, id: string): Promise<void> {
    try {
      await axios.delete(`${BACKEND_URL}/system-announcements/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (error) {
      throw error;
    }
  },

  async getActiveAnnouncements(): Promise<SystemAnnouncement[]> {
    try {
      const response = await axios.get(`${BACKEND_URL}/system-announcements/active/public`);
      return response.data?.data?.announcements || [];
    } catch (error) {
      throw error;
    }
  },
};
